"use client";

import Link from "next/link";
import { useState } from "react";

interface BrandFooterProps {
  locale: string;
  dict: Record<string, unknown>;
}

export default function BrandFooter({ locale, dict }: BrandFooterProps) {
  const nav = (dict.nav as Record<string, string>) ?? {};
  const newsletter = (dict.newsletter as Record<string, string>) ?? {};
  const footerDict = (dict.footer as Record<string, string>) ?? {};

  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setStatus("sending");
    try {
      const res = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error();
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  const cols = [
    {
      title: "SHOP",
      links: [
        { label: nav.products ?? "All products", href: `/${locale}/products` },
        { label: "Age Defense", href: `/${locale}/products/age-defense` },
        { label: "Antioxidant", href: `/${locale}/products/antioxidant` },
        { label: "Sensitive", href: `/${locale}/products/sensitive` },
        { label: "Solar Shield", href: `/${locale}/products/sun-protection` },
        { label: "Bright to Even", href: `/${locale}/products/brightening` },
      ],
    },
    {
      title: "FROM THE LAB",
      links: [
        { label: nav.ingredients ?? "Ingredients", href: `/${locale}/ingredients` },
        { label: nav.routines ?? "Routines", href: `/${locale}/routines` },
        { label: nav.blog ?? "Blog", href: `/${locale}/blog` },
        { label: nav.faq ?? "FAQ", href: `/${locale}/faq` },
      ],
    },
    {
      title: "PARTNERS",
      links: [
        { label: nav.b2b ?? "Wholesale", href: `/${locale}/b2b` },
        { label: "Become a Distributor", href: `/${locale}/b2b` },
        { label: "Influencer Portal", href: "https://app.bpderma.com" },
        { label: "hub.bpderma.us", href: "https://hub.bpderma.us" },
      ],
    },
    {
      title: "COMPANY",
      links: [
        { label: nav.about ?? "About Us", href: `/${locale}/about` },
        { label: nav.contact ?? "Contact", href: `/${locale}/contact` },
        { label: "Press", href: `/${locale}/contact` },
      ],
    },
  ];

  return (
    <footer style={{ background: "var(--bp-ink)", color: "var(--bp-bg)" }}>
      <div
        className="bp-footer-top"
        style={{
          padding: "80px var(--bp-edge)",
          borderBottom: "1px solid oklch(0.3 0 0)",
        }}
      >
        <h3
          style={{
            fontFamily: "var(--bp-serif)",
            fontWeight: 300,
            letterSpacing: "-0.02em",
            fontSize: "clamp(36px, 4.5vw, 64px)",
            lineHeight: 1,
            margin: 0,
            textWrap: "balance" as never,
          }}
        >
          {newsletter.title ?? "Stay in the"}
          <br />
          <em style={{ fontStyle: "italic", color: "oklch(0.72 0 0)" }}>loop</em>.{" "}
          {newsletter.subtitle ?? "Clinical skincare insights, quarterly."}
        </h3>
        <form
          onSubmit={submit}
          style={{
            display: "flex",
            borderBottom: "1px solid var(--bp-bg)",
            alignSelf: "end",
            width: "100%",
          }}
        >
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={newsletter.placeholder ?? "your.email@domain"}
            disabled={status === "sending" || status === "success"}
            style={{
              flex: 1,
              background: "transparent",
              border: 0,
              color: "var(--bp-bg)",
              padding: "14px 0",
              fontFamily: "var(--bp-mono)",
              fontSize: 13,
              outline: "none",
            }}
          />
          <button
            type="submit"
            disabled={status === "sending" || status === "success"}
            className="bp-mono-sm"
            style={{
              color: "var(--bp-bg)",
              padding: "0 4px",
              background: "none",
              border: 0,
              cursor: "pointer",
              opacity: status === "success" ? 0.5 : 1,
            }}
          >
            {status === "success"
              ? "✓ SUBSCRIBED"
              : status === "sending"
              ? "..."
              : `${newsletter.button ?? "SUBSCRIBE"} →`}
          </button>
        </form>
        {status === "error" && (
          <p className="bp-mono-sm" style={{ color: "oklch(0.78 0.12 25)", marginTop: 8 }}>
            {newsletter.error ?? "Something went wrong. Please try again."}
          </p>
        )}
      </div>

      <div
        className="bp-footer-grid"
        style={{ padding: "64px var(--bp-edge)" }}
      >
        <div>
          <img
            src="/logo.svg"
            alt="Beauty & Pharma Dermaceuticals"
            style={{ height: 54, width: "auto", display: "block", filter: "invert(1) brightness(1.15)" }}
          />
          <p style={{ marginTop: 20, color: "oklch(0.72 0 0)", fontSize: 13, maxWidth: 320 }}>
            Beauty &amp; Pharma Dermaceuticals — clinical care, formulated in Freiburg and dispensed
            from Istanbul to 15+ countries.
          </p>
          <div className="bp-mono-sm" style={{ color: "oklch(0.6 0 0)", marginTop: 20 }}>
            MEIER DYNAMICS GMBH · FREIBURG, DE
          </div>
        </div>

        {cols.map((col) => (
          <div key={col.title}>
            <div className="bp-mono-sm" style={{ color: "oklch(0.72 0 0)", marginBottom: 20 }}>
              {col.title}
            </div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
              {col.links.map((l) => (
                <li key={l.label + l.href}>
                  <Link href={l.href} style={{ fontSize: 14, color: "var(--bp-bg)" }}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div
        className="bp-footer-strip"
        style={{
          borderTop: "1px solid oklch(0.3 0 0)",
          padding: "24px var(--bp-edge)",
          fontFamily: "var(--bp-mono)",
          fontSize: 10,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "oklch(0.65 0 0)",
        }}
      >
        <span>
          © 2026 BEAUTY &amp; PHARMA DERMACEUTICALS · EU 1223/2009
        </span>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <Link href={`/${locale}/cookie-policy`} style={{ color: "inherit" }}>
            {footerDict.cookies ?? "COOKIES"}
          </Link>
          <Link href={`/${locale}/gizlilik-politikasi`} style={{ color: "inherit" }}>
            {footerDict.privacy ?? "PRIVACY"}
          </Link>
          <Link href={`/${locale}/mesafeli-satis-sozlesmesi`} style={{ color: "inherit" }}>
            {footerDict.terms ?? "TERMS"}
          </Link>
          <Link href={`/${locale}/teslimat-ve-iade`} style={{ color: "inherit" }}>
            {footerDict.shipping ?? "SHIPPING & RETURNS"}
          </Link>
        </div>
        <span>bpderma.com</span>
      </div>

      <style>{`
        .bp-footer-top {
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          gap: 48px;
          align-items: end;
        }
        .bp-footer-grid {
          display: grid;
          grid-template-columns: 1.4fr repeat(4, 1fr);
          gap: 40px;
        }
        .bp-footer-strip {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          gap: 16px;
          align-items: center;
        }
        .bp-footer-strip > :nth-child(3) {
          text-align: right;
        }
        @media (max-width: 900px) {
          .bp-footer-top {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          .bp-footer-grid {
            grid-template-columns: 1fr 1fr;
            gap: 32px;
          }
          .bp-footer-strip {
            grid-template-columns: 1fr;
            text-align: center;
          }
          .bp-footer-strip > :nth-child(2) {
            justify-content: center;
          }
          .bp-footer-strip > :nth-child(3) {
            text-align: center;
          }
        }
      `}</style>
    </footer>
  );
}
